/**
 * JS Implementation of MurmurHash3 (r136) (as of May 20, 2011)
 *
 * @author <a href="mailto:gary.court@gmail.com">Gary Court</a>
 * @see http://github.com/garycourt/murmurhash-js
 * @author <a href="mailto:aappleby@gmail.com">Austin Appleby</a>
 * @see http://sites.google.com/site/murmurhash/
 *
 * @param {string} key ASCII only
 * @param {number} seed Positive integer only
 * @return {number} 32-bit positive integer hash
 */

export type HasherStr = "murmur3" | "simple" | "stringSum";
export type HashFunction = (value: string, seed?: number) => number;

export type ValuesStr = "random" | "randomLonger" | "randomUUID" | "intToStr" | "stringSumDos" | "randomIpAddress" | "murmur3Collisions" | "commonWords" | "randomIP";
export type ValueFunction = (i: number) => string;

export function getHashFunction(hasher: HasherStr): HashFunction {
    switch (hasher) {
        case "murmur3":
            return murmur3;
        case "simple":
            return simpleHash;
        case "stringSum":
            return stringSum;
        default:
            throw new Error(`Unknown hasher: ${hasher}`);
    }
}

export function getHashFunctionName(hashFn: HashFunction): HasherStr {
    switch (hashFn) {
        case murmur3:
            return "murmur3";
        case simpleHash:
            return "simple";
        case stringSum:
            return "stringSum";
        default:
            throw new Error(`Unknown hashFn: ${hashFn}`);
    };
}


export function getValuesFunction(values: ValuesStr): ValueFunction {
    switch (values) {
        case "random":
            return i => random(i, 10);
        case "randomLonger":
            return i => random(i, 32);
        case "randomUUID":
            return i => crypto.randomUUID();
        case "intToStr":
            return intToStr;
        case "stringSumDos":
            return stringSumDos;
        case "randomIpAddress":
            return randomIpAddress;
        case "murmur3Collisions":
            return murmur3Collisions;
        case "commonWords":
            return commonWords;
        case "randomIP":
            return randomIP;
        default:
            throw new Error(`Unknown valueFn: ${values}`);
    }
}

export function murmur3(key: string, seed?: number): number {
    var remainder, bytes, h1, h1b, c1, c1b, c2, c2b, k1, i;

    remainder = key.length & 3; // key.length % 4
    bytes = key.length - remainder;
    h1 = seed || 0;
    c1 = 0xcc9e2d51;
    c2 = 0x1b873593;
    i = 0;

    while (i < bytes) {
        k1 =
            ((key.charCodeAt(i) & 0xff)) |
            ((key.charCodeAt(++i) & 0xff) << 8) |
            ((key.charCodeAt(++i) & 0xff) << 16) |
            ((key.charCodeAt(++i) & 0xff) << 24);
        ++i;

        k1 = ((((k1 & 0xffff) * c1) + ((((k1 >>> 16) * c1) & 0xffff) << 16))) & 0xffffffff;
        k1 = (k1 << 15) | (k1 >>> 17);
        k1 = ((((k1 & 0xffff) * c2) + ((((k1 >>> 16) * c2) & 0xffff) << 16))) & 0xffffffff;

        h1 ^= k1;
        h1 = (h1 << 13) | (h1 >>> 19);
        h1b = ((((h1 & 0xffff) * 5) + ((((h1 >>> 16) * 5) & 0xffff) << 16))) & 0xffffffff;
        h1 = (((h1b & 0xffff) + 0x6b64) + ((((h1b >>> 16) + 0xe654) & 0xffff) << 16));
    }

    k1 = 0;

    switch (remainder) {
        case 3: k1 ^= (key.charCodeAt(i + 2) & 0xff) << 16;
        case 2: k1 ^= (key.charCodeAt(i + 1) & 0xff) << 8;
        case 1: k1 ^= (key.charCodeAt(i) & 0xff);

            k1 = (((k1 & 0xffff) * c1) + ((((k1 >>> 16) * c1) & 0xffff) << 16)) & 0xffffffff;
            k1 = (k1 << 15) | (k1 >>> 17);
            k1 = (((k1 & 0xffff) * c2) + ((((k1 >>> 16) * c2) & 0xffff) << 16)) & 0xffffffff;
            h1 ^= k1;
    }

    h1 ^= key.length;

    h1 ^= h1 >>> 16;
    h1 = (((h1 & 0xffff) * 0x85ebca6b) + ((((h1 >>> 16) * 0x85ebca6b) & 0xffff) << 16)) & 0xffffffff;
    h1 ^= h1 >>> 13;
    h1 = ((((h1 & 0xffff) * 0xc2b2ae35) + ((((h1 >>> 16) * 0xc2b2ae35) & 0xffff) << 16))) & 0xffffffff;
    h1 ^= h1 >>> 16;

    return h1 >>> 0;
}

// https://gist.github.com/jlevy/c246006675becc446360a798e2b2d781
export function simpleHash(str: string): number {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = (hash << 5) - hash + char;
        hash &= hash; // Convert to 32bit integer
    }
    return hash;
};

export function stringSum(key: string): number {
    let hash = 0;
    for (let c of key) {
        hash += c.charCodeAt(0);
    }
    return hash % 1000000;
}

export function intToStr(i: number): string {
    return i.toString();
}

export function random(i: number, length: number): string {
    return Array.from({ length }, () => {
        return String.fromCharCode(Math.floor(Math.random() * 26) + 97);
    }).join('');
}

export function randomIP(i: number): string {
    return Array.from({ length: 4 }, () => {
        return Math.floor(Math.random() * 256);
    }).join('.');
}

export function stringSumDos(i: number): string {
    let badValues = ['5e39760e-aa2a-4610-96fa-3967ed7ab136',
        '6b678874-34e6-4d1f-bfc2-149b4b52c02d',
        '6ef9e607-85f8-4006-8aa2-602fc41ac7e1',
        '95932add-d1ab-4a5a-9235-1668b9a65c73',
        '5d253c76-47ea-4c7a-a36e-df546060259c',
        '8a23a67d-c255-44b8-80a5-bce612985b4f',
        '5ec04fb9-a555-4f11-ba49-71169b4e0d87',
        '9d6fe6b9-1123-4cd6-8105-1d4ae3da9537',
        '48cd4c24-ff66-42a2-94af-f413e243c637',
        'ee5335ad-d640-4139-9ef6-e52de217a705',
        'e949ee35-1bd6-45e1-8902-f2355f3aaa16',
        'cc2c1a6e-c449-426f-8fe4-76d57523c320',
        'aec2408f-74c7-4951-9427-1bed70cc807a',
        '300af7f6-642a-4b14-ba8d-079396d54ce9',
        '09ebc11e-7f02-41be-82f1-5f795329e28a',
        '900d6f88-96af-4654-aa1b-530d10f474fe',
        '7a67445a-07c3-4d2d-94a6-3ff79127cda1',
        'd0065773-ab5a-4213-9e78-767c2ea1dcf7',
        '151b609d-05e2-4a2e-87f4-8c8bed386a34',
        'c9a0d3a7-07de-4427-8915-a3745ef1cb56',
        '80224a93-a8be-4354-b17e-542f3f2ee9a8',
        'd32c217e-c2c6-49c3-af13-5225e68798ac',
        'd95e0dda-578a-4bb7-84c0-b045415952f6',
        'ad16b72f-79f3-4f63-83a0-5c63140a9f8a',
        '3b846a4d-5e63-4191-a371-44b7a5c9f6cd',
        '08a0b8c2-8d9c-4213-9df8-4ff5f01218c5',
        '31e72e3e-993d-4555-ac1c-3a7b57e352c6',
        '5a80679c-db36-4b97-974e-208700ceabc0',
        'e1aa0c9c-6389-40bb-a5fb-72b253592987',
        'f1d19de1-da44-4736-a12b-59678317bc5f',
        '718a057f-3404-4082-8955-cd5c9d0efada',
        '45df8e6b-07df-42c4-bb72-5391b900a919',
        'e54b1ea5-cc10-4077-8969-019ebcb87e50',
        'db0a070e-b92a-4486-b2f6-6cf21c997483',
        '1d6809ec-6d1a-4638-84a5-4dbe170922ed',
        '7884712f-4dbc-46fa-bdfc-5b0499355011',
        'ff325df0-9381-413e-a90b-58d5f57425ab',
        '1a977ddd-9a71-4c43-8231-82b39c6c53da',
        '201d97ea-68a0-4249-b6ec-78a63c574bb5',
        'fc09ad93-40c2-46c5-a042-2ffc1249899d',
        '7ea95342-82a6-4036-81a1-8f52deeef23d',
        'b46d1a5a-679e-42b4-9405-fdf90b83f232',
        '3101ffe8-ac72-469a-a457-7b6d3fb73372',
        'edc4086d-25b0-4c4b-9af7-03f1848913a9',
        '4073daf2-e3d6-4819-bdb2-c63a5b689165',
        '28a06f94-b334-49b5-8dc8-177c062baab9',
        '796b16c1-4312-4be9-8c0f-e5b957c1a15f',
        'ff519676-7cf1-4583-ba6e-196c4a2cc210',
        '370f0bd0-8487-41a4-95fd-7d807cc5e12d',
        '12f0aa65-57c0-4f7e-9516-f990c2ab39d4',
        '187ab333-3903-4f52-af35-f708fa63bee8',
        'bdb95630-1b47-4e82-8e52-af16368a27af',
        'df0dff30-447b-42b9-9164-0bc08e80e954',
        'e4f75735-34a1-457b-a8ed-74ccd71f0523',
        'f405aa12-438e-490e-bfe6-297710dbd586',
        '5d1706cc-1d26-4df0-bb82-7f13b65985f4',
        'a0df1e9e-4824-4043-9a6b-1e8c65493cd3',
        '565c8a89-f3b0-4690-a1e8-3c50fe5d8c00',
        '292416bd-6e65-406a-b780-c6d1fa86e8b2'];
    return badValues[i % badValues.length];
}

export function randomIpAddress(i: number): string {
    let ip = Math.floor(Math.random() * 2 ** 32);
    return `${ip >>> 24}.${ip >>> 16 & 255}.${ip >>> 8 & 255}.${ip & 255}`;
}

export function murmur3Collisions(i: number): string {
    let strings = [
        "pwsgcwhone",
        "myoniuqwan",
        "jqnjnoiane",
        "whwjbjrler",
        "cnvptodone",
        "hcqwxqnger",
        "snxladoone",
        "tjtfkwaman",
        "blnwnmsere",
        "azqmcpnder",
        "fndvlznner",
        "sruzkfcran",
        "iitejqbler",
        "omhhddgger",
        "gfvhlqhull",
        "khekxwdger",
        "qmemcssman",
        "lddaxcdull",
        "vlvcyecter",
        "fevoovaber",
        "ddrggozman",
        "urcurvaine",
        "jvmabsgler",
        "wvjroczhan",
        "ifsacarker",
        "ylwddlnull",
        "cbfcmjyter",
        "jorgrhwyan",
        "chrfkcufer",
        "cltnpopzan",
        "dowxpheper",
        "glpymdizan",
        "xtfmzuohan",
        "urfmhpqker",
        "xczpkfeger",
        "ndazdubker",
        "xfifbxljan",
        "twpobjlran",
        "hgwmlsyzan",
        "dilkguyine",
        "zcegsjpner",
        "jtskkovfer",
        "pwyjujvfer",
        "xjdnmnzjan",
        "dcvniedwan",
        "pszkqgbwan",
        "scosorywan",
        "rkeojfcxxx",
        "pdmbjgmver",
        "ocflqqtxxx",
        "lrvluvgxxx",
        "zujyrlpqus",
        "yaeezrrxxx",
        "aaoinyjqus",
        "ksixpirdan",
        "jvcgwvckse",
        "jkshspqfer",
        "oythnvhare",
        "myqbffflll",
        "rmbjxbwier",
        "cglhndtoll",
        "imnkckndan",
        "aahwuyjese",
        "luxydmhier",
        "ejxqjqndan",
        "tfrkovmyse",
        "xspagdkuse",
        "mqnsplfier",
        "etqmtinoll",
        "yylmallser",
        "pubvzkemer",
        "quwxjapher",
        "rqiohatuse",
        "ismffiyffe",
        "pslqhieche",
        "dswpkrezza",
        "xawejmlmer",
        "agqiggjser",
        "kxqbjnkher",
        "fyedqucbya",
        "isoxhfnzza",
        "ibfhcjytan",
        "ioifrquvin",
        "helbzzwmer",
        "mzustzpzza",
        "wybevoaese",
        "etlwdnxher",
        "mdtnhpmjon",
        "yhiuaooxya",
        "aujursuxya",
        "lxhxowdpan",
        "xsfuulrpan",
        "yexbevaffe",
        "wtyahdaffe",
        "jvmnqmcvin",
        "xoknhvyher",
        "cazcytjvin",
        "txhkcezjon",
        "ogrfkeuwne",
        "hbiwrlqure",
        "pscnxmesse",
        "apssulynge",
        "sfbzrkpqma",
        "uuzhwggite",
        "zvakyimite",
        "sxibgzchin",
        "jmlqaznite",
        "apfuhchton",
        "tkqttolore",
        "sclkflmsse",
        "ctymylgsse",
        "giyeouosse",
        "xqmwhyiore",
        "dfcugvhlan",
        "vwdwziwkin",
        "saqwurvban",
        "cicavqwdon",
        "yrcufcivan",
        "tzlpoyyure",
        "tyheejcmon",
        "eyzkqyivan",
        "dfdlrcnmon",
        "whgpzetkin",
        "rjapaorpin",
        "psinjifjer",
        "gxlsscekin",
        "kdnqfryite",
        "lxmhiomgin",
        "hrbwojrpin",
        "yjnjqnrpin",
        "jpqliuvjer",
        "qgawmpwfan",
        "kkevyyejer",
        "jqoqfmxwer",
        "dqymmiuure",
        "vxxkmjzfan",
        "rqcftecxin",
        "duoncnpvan",
        "vjxfviywer",
        "vanjiqxyne",
        "yzhvdqxxon",
        "dwrdbponde",
        "vtnyliande",
        "vxagmqffin",
        "qvnehachon",
        "gajclcshon",
        "rmvazpooon",
        "wkixrihrin",
        "ifllvzmmin",
        "upclioylon",
        "oczcndsoon",
        "zdmvahacer",
        "eiogqcmune",
        "xkqacvymin",
        "sdevkwspon",
        "lfjrwalmin",
        "nhxnfqbhon",
        "lvqgffvvon",
        "sqabtxubon",
        "dhuifoocer",
        "odpvpoaian",
        "idrvqtoene",
        "iflosidthe",
        "anchzxgzon",
        "dwxvvjokan",
        "ebucgusdya",
        "wwgvwhvjus",
        "agwqfkqxon",
        "tgwytkrzon",
        "bcouofzpon",
        "vimgvjkwin",
        "eqtikewzon",
        "bvntlvmfin",
        "sdtoobefin",
        "ijjdcvbvon",
        "tdvskdfkan",
        "rtfxbtlxon",
        "kkmllekqqu",
        "npsguskqqu",
        "lfyrkqkqqu",
        "gndjmzzzin",
        "wfiabqbose",
        "kjbsjcbyer",
        "iwuxgxrean",
        "gnmfucbose",
        "bmfrzclise",
        "hpqtpcgase",
        "palhqjjron",
        "xugduqotte",
        "ahhapbjute",
        "auaxfxicon",
        "ufjdssmmbe",
        "paejoqvsan",
        "vpxnrqjron",
        "ailumnhyer",
        "jggviowlin",
        "vthowwwkon",
        "giebtgtsan",
        "mueeikclin",
        "ibguqtclin",
        "afjdmxpute",
        "nkvxdpkmbe",
        "drqjxaydin",
        "qiclfaccon",
        "pvypckikon",
        "kjrlxxftte",
        "jhphztgtte",
        "bhzjrnftte",
        "xverrmdfon",
        "iqbhbjgnin",
        "yhbxmcocon",
        "udxllxrkon",
        "blmmzydhte",
        "ehhfsrukon",
        "ywkgaoxron",
        "vcxddyyphe",
        "qnliclkphe",
        "ohkuvebkon",
        "zvttfbgphe",
        "cvwjfkbghe",
        "wfobcmofon",
        "bnlhvlxwon",
        "ifdqghrhte",
        "dgkgvyzjin",
        "zozjzphvse",
        "runhbfqvse",
        "fbibxzcxan",
        "mllztbjxan",
        "meouuyywon",
        "sbgoljbate",
        "xufbeejqwn",
        "ezfxofwate",
        "ubqtacusin",
        "wexdnlvous",
        "sfenzqtous",
        "dcmkueueer",
        "jvfwbjatin",
        "jahcightin",
        "dijntyqmya",
        "oejdnaxate",
        "urmalkamya",
        "rmupjmqqin",
        "zhnbkrbick",
        "xhgvwqisin",
        "fdprnokyll",
        "quwjfpylya",
        "bmaekzucin",
        "qnkrijmbre",
        "hzxmmekmya",
        "kjzrgcnous",
        "yszdtatrde",
        "udwjczclya",
        "ryojwaopse",
        "kyutunugse",
        "bmswvxcyll",
        "rfzgfwecin",
        "ztqcmlzsin",
        "jgrioozrde",
        "tvjxmgxrde",
        "qhmefwnjre",
        "hkqxpjoate",
        "zmnyzadwse",
        "typxzdmxck",
        "nzhlxtrwse",
        "xfwyoyqlya",
        "lonzuubede",
        "qadculvqin",
        "svbwyhtvya",
        "vafxfyquma",
        "wzfccbfqin",
        "yfsrzualse",
        "uqgcgbrede",
        "qhbajtampe",
        "xvnbwilown",
        "hcvaolgown",
        "nsdnnqwlse",
        "nyfqkfcrte",
        "jtgidcnown",
        "jdducohmpe",
        "dghllpycus",
        "ukgmyigyde",
        "povanxmhne",
        "fxfchutlse",
        "cdbjrpecus",
        "ylwxawqhne",
        "ktloapzrte",
        "diouwshdre",
        "cvgqvgbmpe",
        "rnwxbmjlse",
        "sfiannauma",
        "orljcudtse",
        "avbxumtwhe",
        "igtcebyjke",
        "eqkkeriuma",
        "mpxszyoade",
        "eeshdvwjke",
        "uttptfhwhe",
        "npchtyvtse",
        "curqstzwhe",
        "kzybnchzus",
        "xjpgiggjke",
        "bjvisjiyde",
        "mszbwjvkya",
        "quskkgzqon",
        "pzzukymqon",
        "yybijdcqon",
        "eyerdjgqon",
        "lembntjove",
        "vvpaqnhove",
        "wcstiighya",
        "qijeugreya",
        "lqjfjmdrya",
        "oonamlxcre",
        "zzhnnbxcre",
        "tvgareggge",
        "ehwjbgpppe",
        "bjmorhjhya",
        "gbrpiwncre",
        "jrmbcpqeya",
        "mtesgiocre",
        "etrvzwxppe",
        "jclobqaion",
        "fsvczeexer",
        "erqvdjmnon",
        "mffepyebus",
        "haddosyybe",
        "qwnvstyuck",
        "ntbltzhgge",
        "crzylwzcre",
        "wovuyqxrya",
        "waddzrvhya",
        "yafwcfpjja",
        "jeqovyykus",
        "dxxkiidzma",
        "hivcdsnfte",
        "vcdufgeove",
        "segfivmwke",
        "lkpdwupfte",
        "tnpegdajja",
        "gtxkvzfnon",
        "jqaqxdqfte",
        "iesrvmfzma",
        "ywrnmjlxer",
        "qvvwxbjxer",
        "fxgviesqte",
        "vceqyvjete",
        "uppkhslabe",
        "oqtxhyyote",
        "vumrifisma",
        "cwutgjosma",
        "mvxvdxisma",
        "whbjwnumus",
        "sogodvjmus",
        "fnwmdeggre",
        "bxexbasmus",
        "mzlyjafmus",
        "uwgfmvidus",
        "paevkzknke",
        "rndszaktre",
        "gaidzptzll",
        "xakaqbztre",
        "oxjdmyfsma",
        "feuhvxygre",
        "ezqxuibfre",
        "wenmvswdus",
        "fjszgpjnke",
        "jrsgxshgre",
        "rmgvjxohre",
        "oafjwdgzll",
        "llxtlggzll",
        "tniudujgre",
        "somkfljtre",
        "asefaczbbe",
        "ehabgfcnke",
        "zitddgsdus",
        "atskgndkke",
        "ikvaxvfjhe",
        "dprndojyma",
        "kjueaweuin",
        "cbdvthfkke",
        "lidytfajhe",
        "igyrotaxse",
        "pxjqkhujhe",
        "ouwqrafkke",
        "cbiftlqvma",
        "smnhirvqer",
        "ycyvadhqer",
        "xongororse",
        "efapbiqmse",
        "iohdkdueve",
        "osgakrxide",
        "zuhiuawode",
        "yszmgwqupe",
        "rycchpmyck",
        "wogjqzayck",
        "whfcoeamse",
        "jrixsguide",
        "mospydsode",
        "enmdrgcrse",
        "lrjihuayck",
        "lpvdvarsck",
        "njsqzlkhll",
        "emniupycll",
        "mewdnuncll",
        "nrflorvbse",
        "wuujamgbse",
        "qsttgobmse",
        "jeaqwwowwn",
        "ekpgjwkcll",
        "wvaasupzhe",
        "bbgmdrgzhe",
        "qssjulcvde",
        "lexxzylrse",
        "btlyzhgwwn",
        "lxfuerawwn",
        "wmiipvxwwn",
        "cbljhzgvde",
        "ffnvctszhe",
        "vsjbxnvfll",
        "ztkzfkiwwn",
        "iiizofcwwn",
        "jgcyukdqse",
        "ktktfdgxpe",
        "fhxrabvxpe",
        "paqzwypain",
        "xgodvselma",
        "fvseihkima",
        "kwjexqvain",
        "lgdumqlema",
        "pswqpqutck",
        "gjfgjqndll",
        "kmosdaxtck",
        "fvyrcmvrus",
        "ujijqnluan",
        "gmrasrxema",
        "tofwdiprus",
        "vbqubpvuan",
        "bkognqskne",
        "hkruazemck",
        "iearrvifya",
        "dzymrpqrus",
        "sqkrhjmrus",
        "uwgtswxdll",
        "mvkvxxnhse",
        "vuoostxdll",
        "vigrvonmck",
        "zjbwhpbmck",
        "revnrfypya",
        "jcpjfvktck",
        "zkvbwmjpya",
        "jhqlntagya",
        "dffnaxkbck",
        "qpepxifvbe",
        "uobfrjrpya",
        "xfpnvifbck",
        "kpjvyzhbck",
        "qcakqxpnck",
        "ojucicxkne",
        "cfuzlfijck",
        "hmlfrnvjck",
        "qajfkweoma",
        "heqwwavtck",
        "zpsuchrwya",
        "dvlhkyrvbe",
        "urfslopwya",
        "gqjsyzczte",
        "kinfpsinya",
        "tummpfyqpe",
        "zemtjztvbe",
        "xwlgubrdma",
        "tjdaingqpe",
        "kafcwttake",
        "jyyvishxza",
        "judraspoya",
        "fmgjprmeza",
        "bjfjgjwoya",
        "yyaqlgpife",
        "qlveelrake",
        "xwdelidife",
        "mibhwcchma",
        "tdfxaychma",
        "ixorrseyre",
        "cjbqobucya",
        "hgcwfzhnya",
        "kswgdkkkhe",
        "ffndnvwhma",
        "vmpsrtclfe",
        "xpydaqruge",
        "inosfxcdma",
        "wgijqkecya",
        "cftvrejdma",
        "fmicjjvdma",
        "kaywdfqkhe",
        "sgssxxhgte",
        "klaomorpte",
        "hasodwwyre",
        "lvdyhxmgte",
        "ovflgnmjne",
        "rqhglqauge",
        "ppxhtjwyre",
        "rkuuwmkzxx",
        "kwjzzxtgte",
        "xipnygmzxx",
        "qtbjnmsdma",
        "cefojsjfse",
        "baqhnqkwre",
        "teblnrzwre",
        "cvbtjorwre",
        "gqkifwtpte",
        "xgrqaabxde",
        "hlmwfhsvza",
        "tzrhccozxx",
        "updgczfqbe",
        "byjoqblqbe",
        "rzqlajnqbe",
        "vqxxrynvza",
        "aamqqvbexx",
        "zjsmkqfexx",
        "macxyttexx",
        "dptzfzlexx",
        "zhbyblwahe",
        "btzcomtexx",
        "itlpbzwige",
        "vtwlffasya",
        "lfvbjpenhe",
        "xgicgtlyza",
        "yuvulggsya",
        "kuzajeutll",
        "bqocsshrke",
        "ndtrrggsya",
        "linlsoodge",
        "noogfubhhe",
        "chyplsjsya",
        "netwjfvvll",
        "jjgmxshhhe",
        "rcnhricdge",
        "udpnoywwge",
        "labdadpcck",
        "rfbifjqcck",
        "ozkdtdtmne",
        "xamgdbxrke",
        "rmpiymuyza",
        "hdhuzryzse",
        "rwimgxcfma",
        "snnhnlkpck",
        "axyhwqrpck",
        "amnzhtrhhe",
        "jmkisqyige",
        "mrfysdmkre",
        "elptmkhwge",
        "hmhjogzwge",
        "zzdybqrjte",
        "unhviutqke",
        "vmydjopqke",
        "exytgldvll",
        "zdnkhjbxus",
        "omdiubsqke",
        "eljuygaxus",
        "zlzythzvll",
        "pxcwwestma",
        "sqgdiniape",
        "tlxaqearma",
        "crsuyzjeck",
        "fnflyamyus",
        "eqhjmtqmte",
        "bwfweiseck",
        "cyvesochbe",
        "rerhmlmsll",
        "dqqhukayus",
        "jkolcgybde",
        "jptcdoqike",
        "ztbdcxmyus",
        "ickzzanyus",
        "npdfgcjhbe",
        "smkqytbsll",
        "wmijdkmnus",
        "bgnwukdmte",
        "iqzeceidbe",
        "afzajhdbde",
        "fnqistwrma",
        "yubdpzzsll",
        "yqrjjqkcma",
        "eizkklcdbe",
        "yjtnzbbmte",
        "ecylmouvck",
        "vxxarttdbe",
        "xikrllykbe",
        "jzbcijnbde",
        "zpbpcywyus",
        "bsntvdrfge",
        "usfiwlyzke",
        "qungseeope",
        "ersfnyekbe",
        "pwuhxxlgbe",
        "xfgjickzke",
        "iuayewuyus",
        "zgdlvuiude",
        "paftqoazke",
        "jvyzrwnxma",
        "fikuoncwte",
        "swmeznvkbe",
        "zwfdbimwte",
        "jelqkdxjse",
        "scuxwkzfge",
        "dnsyvtgvck",
        "oafohusvck",
        "bijuqxrwte",
        "owzogarqne",
        "xkigjjerge",
        "tsclalusbe",
        "ljcvawanza",
        "towatyssbe",
        "kvfgtovage",
        "nzgfawwebe",
        "ssdgslchke",
        "kghagllipe",
        "jhnifqgage",
        "cvfitjasbe",
        "bfvdpymipe",
        "cansqicuve",
        "nalajsbuve",
        "qdgtayovve",
        "bmhlcswsbe",
        "qvkhejayte",
        "uorztgmbza",
        "eqftunlsbe",
        "alprhmslbe",
        "gihygbezre",
        "eqyffgzmll",
        "ycebuhuvve",
        "zvfgiaowma",
        "nmhgotlbza",
        "xjwfqnapza",
        "niicsjhkte",
        "qmidyixrge",
        "cjejtpcbza",
        "cbetjnfvve",
        "ntuyohzbza",
        "ddnwcgqkte",
        "vbgvbwvpza",
        "btrcbekzre",
        "txyqmtmzre",
        "ccodnfyhke",
        "xsdcnmzwma",
        "xagmtmajbe",
        "nvtzuyzwma",
        "cdufolmqre",
        "zivudnjxll",
        "bfkkltdqre",
        "vyayjomave",
        "hhuskwqwbe",
        "opuzcdwave",
        "dahbysfepe",
        "uhftpexxbe",
        "zjxzuszepe",
        "yinwiyyoge",
        "bvmcavvsde",
        "plrtisdave",
        "xnzrnnqmhe",
        "sjyywqiepe",
        "tdriwbutbe",
        "vdizvmlyya",
        "ijgidholck",
        "icyzluqqhe",
        "rywuevntbe",
        "utitiufcpe",
        "tymojxbsde",
        "cyptjozxbe",
        "rwggfshyya",
        "wruqvgtzck",
        "tmblygcsde",
        "rcqpmzjcpe",
        "bvipjhsyya",
        "lnorhntbma",
        "oqtykgvzck",
        "gmralbvlck",
        "gofrstlvpe",
        "tfxnysjdja",
        "lhaxhpbgma",
        "rhsnyjypke",
        "krdyxssgma",
        "sgrxlujwbe",
        "xwuyatwjde",
        "txmfgqvtbe",
        "hqkgnhewbe",
        "lpooxupfbe",
        "yuxrixlhck",
        "fgcpffxjde",
        "sqfvzznvpe",
        "ovrfsuqfbe",
        "gjrtkkyoer",
        "zcpqwrmein",
        "ngzzdepaus",
        "yhnhflngde",
        "xkdvdoatke",
        "xhjeckroer",
        "hblnfiuiza",
        "tptzzhvsre",
        "wegtbujuke",
        "pqznbqloer",
        "lbqqlkmyge",
        "hxyvvbwlza",
        "msoipwsmde",
        "zfheohwkza",
        "tkevdbzmde",
        "fvmkwjskza",
        "ugohfhjlza",
        "figoobigde",
        "lmsefprkza",
        "ixhfymexre",
        "vfvevfwdhe",
        "yfqwexkcbe",
        "csduavfhge",
        "ybhitibzne",
        "ggyzjwokza",
        "nvenenwjpe",
        "fhuycmdtke",
        "daajvgoaus",
        "evedfdjyge",
        "frchhsfpde",
        "iahsppqdhe",
        "wcnjerbwde",
        "qmaqpdmqya",
        "akeheyjzne",
        "rzreayzvhe",
        "cdglgbvqya",
        "ymcfesdoke",
        "dbkjcezqya",
        "vhcmxbchde",
        "nzptfskoke",
        "ixuuqixius",
        "usapxertja",
        "kneoiadsne",
        "jcdlfwdsne",
        "fsqyqkzoke",
        "rsadgioype",
        "ootlgpabge",
        "fhtqsbmsne",
        "ylrksukmza",
        "dljeoyhnma",
        "zmflbqclpe",
        "qczmvcedza",
        "mbctojivne",
        "cjndjaexfe",
        "oijqwqvdza",
        "wbrksaohde",
        "cfuchitjll",
        "wmteecwmza",
        "owdambcjll",
        "kofhncqcge",
        "wkfsrsoewn",
        "sqigqxcpge",
        "jjkjmjsnma",
        "lmgycbkbge",
        "uvvaccmdza",
        "bgjejzvjll",
        "qesjzahvne",
        "zglibxgwus",
        "peoptydnma",
        "ioxabtejll",
        "eozanqtfhe",
        "nliekaeuya",
        "yxeubedvne",
        "qlmgcxuoke",
        "keftfegqde",
        "jdtjeldxfe",
        "inqzzrzvne",
        "pmysgxlqde",
        "jnfdgkrdke",
        "bgrnbltohe",
        "dqbvtzfuza",
        "bemsugzzde",
        "ufsuaoelge",
        "zbfukeyege",
        "alnahkflge",
        "viwlbmmixx",
        "hmbrxtlyin",
        "tdurjuirpe",
        "jevekdjrpe",
        "aqclvfeege",
        "prhsdwxohe",
        "driimdpuza",
        "rlprjbzxhe",
        "blzgygoyin",
        "mhrmopikck",
        "lcrdutpyin",
        "hirwquvvte",
        "heccfevvte",
        "heggzyccfe",
        "umlmofcsge",
        "pbplahdgfe",
        "nheyxoktwn",
        "axxqagigfe",
        "jfbaiuktwn",
        "lsfytqdcfe",
        "mlckqscgfe",
        "stbmuaizde",
        "utfviylzde",
        "gvjfkpkdke",
        "kqlclcoaza",
        "fykvwapgfe",
        "onrwswjtwn",
        "zswazjbgfe",
        "pyyxvkwjge",
        "alxvxdmgfe",
        "ndyjoshpbe",
        "thpelncnbe",
        "hqiwbtlnbe",
        "favxosuwck",
        "gmfyejqlge",
        "ngxqapjxhe",
        "sveeryflre",
        "iudjytpaan",
        "xzynwgmaan",
        "tsrnxsarve",
        "jognhlneke",
        "muuqplhqge",
        "svkzdrdeke",
        "rfzdpvmqge",
        "btenidngck",
        "ccttmqelre",
        "qavdvwaswn",
        "wqytqpfuxx",
        "bsdkcxzoan",
        "yznokufiin",
        "dvghqsroan",
        "sumuwbluxx",
        "lclxqmmbke",
        "bikkfllswn",
        "okcnurrtne",
        "hkddirblre",
        "gkpfhjppfe",
        "dehwpdolre",
        "sugylqjcja",
        "vkhrmxidfe",
        "ukjtjehlre",
        "prptoydbke",
        "kgypgtglre",
        "tqofqoccja",
        "kzdhzvptne",
        "hzovbozswn",
        "xnmhwtxdfe",
        "kthjntgzge",
        "skrvbbdcja",
        "pwyxffipfe",
        "czcyzzlcja",
        "scwlxorbke",
        "gnufgdfvge",
        "zzstavdtne",
        "nrbdxvnrve",
        "ohpqvzrfja",
        "krafhktnre",
        "pppimpfjve",
        "crsppttnre",
        "coudbsejve",
        "ojrpdexfja",
        "gaztiusxne",
        "tjwnmxzpfe",
        "flznqfbtne",
        "sazrxzlnre",
        "ahgtokvkja",
        "xjukboquhe",
        "zidrpxonpe",
        "klhglcqfja",
        "xsxbmineon",
        "pfxcgozvge",
        "rpxpcdllhe",
        "ygrhoaenpe",
        "yxnuddqawn",
        "zvtquiorza",
        "duwxsgooxx",
        "pbomegzoxx",
        "chnpzyvsja",
        "khpsxdvrza",
        "heuufxosja",
        "upsjdbnnpe",
        "nbmuaeirza",
        "usbfevryke",
        "odkdcjucne",
        "khxxxoadne",
        "wszuuhmbte",
        "ippdskdbte",
        "jvglgtzkwn",
        "ovabkztmke",
        "mmbhsgwtge",
        "imsmfhhhfe",
        "phhlquhkwn",
        "lxztgehkwn",
        "dqgqqwfyke",
        "dhvucjhhfe",
        "qjtiojtkwn",
        "dcwzxrjlhe",
        "qusntalpwn",
        "dnaldbwuhe",
        "peohasbgpe",
        "ojsnklzjwn",
        "abovimmzfe",
        "xdckrpmfke",
        "ucfmrthwpe",
        "jogumerjwn",
        "rzrroqrxke",
        "zkhwxzrxke",
        "tmaqxxiqja",
        "fmgewmhqja",
        "gkawuhkoin",
        "taseuqgiya",
        "pmcniecafe",
        "hfjhvhflne",
        "dqxvyxenfe",
        "ecijggxafe",
        "qnrwossiya",
        "raekpveafe",
        "vaapdpxafe",
        "lppdmegsfe",
        "ivcodxqiya",
        "lycwhjluja",
        "mzhhakggwn",
        "siznywbbpe",
        "lhgropluja",
        "ekwpfmwhwn",
        "mcvwyoxtpe",
        "dtafykqsfe",
        "ehotycakfe",
        "nzrmljclne",
        "lobueaptpe",
        "meqqikzmfe",
        "rthvxejbpe",
        "aiotdrgyhe",
        "vktpzlfgwn",
        "khjwqvltpe",
        "egjexojsfe",
        "kupjkjdjfe",
        "obuifunlne",
        "mdcqaheiya",
        "vbnpwwsbpe",
        "ojtsgrkjfe",
        "emyqicepne",
        "jxehducnfe",
        "vmyupufpne",
        "zdpeagmkfe",
        "zushnfdxja",
        "velbjfwqxx",
        "fmmvyerxja",
        "qbofmqnxja",
        "vsztxjrjfe",
        "jqulyrvqxx",
        "jelepsdvke",
        "fspyikpqxx",
        "fivdlxpqxx",
        "gepjxueqxx",
        "ymhqysgxja",
        "labodttefe",
        "oevqgyjefe",
        "wujkwowofe",
        "ddwvpkayxx",
        "wrpqzaasxx",
        "xkpqlmcofe",
        "sgknqxqaon",
        "kyynhyttfe",
        "wejiudgofe",
        "tkkcvpkuus",
        "zizifqzuus",
        "ulzyqemmwn",
        "oedowaycza",
        "ildadqzkge",
        "lvbtgllmwn",
        "kjwacbrrja",
        "shprbvjhve",
        "mgvinracza",
        "msxhlgqpja",
        "itettvjdwn",
        "vceppqecza",
        "rrxblzmhve",
        "belbqkwmwn",
        "sglacoowqu",
        "rsfrfsxhve",
        "nnqellsrja",
        "bnovcgmjya",
        "jyhwrarfza",
        "runqjejfza",
        "kronhejwqu",
        "yrkjtrvtfe",
        "lhpcqazdwn",
        "cxglkybkge",
        "ubhroldvfe",
        "pjikwaqvfe",
        "tfyciordxx",
        "caaclubqck",
        "pathzdmehe",
        "dfgifrysve",
        "lfkmcbzzwn",
        "evjwjsysve",
        "kaxnzglehe",
        "qnfqfbfnga",
        "njgwngqdor",
        "dqxneazjes",
        "twexcyiwes",
        "qthwjmcwes",
        "nmchfkjxie",
        "rqcfvszfar",
    ];
    return strings[Math.random() * strings.length | 0];
}

export function commonWords(i: number): string {
    let words = [
        "the",
        "of",
        "to",
        "and",
        "a",
        "in",
        "is",
        "it",
        "you",
        "that",
        "he",
        "was",
        "for",
        "on",
        "are",
        "with",
        "as",
        "I",
        "his",
        "they",
        "be",
        "at",
        "one",
        "have",
        "this",
        "from",
        "or",
        "had",
        "by",
        "not",
        "word",
        "but",
        "what",
        "some",
        "we",
        "can",
        "out",
        "other",
        "were",
        "all",
        "there",
        "when",
        "up",
        "use",
        "your",
        "how",
        "said",
        "an",
        "each",
        "she",
        "which",
        "do",
        "their",
        "time",
        "if",
        "will",
        "way",
        "about",
        "many",
        "then",
        "them",
        "write",
        "would",
        "like",
        "so",
        "these",
        "her",
        "long",
        "make",
        "thing",
        "see",
        "him",
        "two",
        "has",
        "look",
        "more",
        "day",
        "could",
        "go",
        "come",
        "did",
        "number",
        "sound",
        "no",
        "most",
        "people",
        "my",
        "over",
        "know",
        "water",
        "than",
        "call",
        "first",
        "who",
        "may",
        "down",
        "side",
        "been",
        "now",
        "find",
        "any",
        "new",
        "work",
        "part",
        "take",
        "get",
        "place",
        "made",
        "live",
        "where",
        "after",
        "back",
        "little",
        "only",
        "round",
        "man",
        "year",
        "came",
        "show",
        "every",
        "good",
        "me",
        "give",
        "our",
        "under",
        "name",
        "very",
        "through",
        "just",
        "form",
        "sentence",
        "great",
        "think",
        "say",
        "help",
        "low",
        "line",
        "differ",
        "turn",
        "cause",
        "much",
        "mean",
        "before",
        "move",
        "right",
        "boy",
        "old",
        "too",
        "same",
        "tell",
        "does",
        "set",
        "three",
        "want",
        "air",
        "well",
        "also",
        "play",
        "small",
        "end",
        "put",
        "home",
        "read",
        "hand",
        "port",
        "large",
        "spell",
        "add",
        "even",
        "land",
        "here",
        "must",
        "big",
        "high",
        "such",
        "follow",
        "act",
        "why",
        "ask",
        "men",
        "change",
        "went",
        "light",
        "kind",
        "off",
        "need",
        "house",
        "picture",
        "try",
        "us",
        "again",
        "animal",
        "point",
        "mother",
        "world",
        "near",
        "build",
        "self",
        "earth",
        "father",
        "head",
        "stand",
        "own",
        "page",
        "should",
        "country",
        "found",
        "answer",
        "school",
        "grow",
        "study",
        "still",
        "learn",
        "plant",
        "cover",
        "food",
        "sun",
        "four",
        "between",
        "state",
        "keep",
        "eye",
        "never",
        "last",
        "let",
        "thought",
        "city",
        "tree",
        "cross",
        "farm",
        "hard",
        "start",
        "might",
        "story",
        "saw",
        "far",
        "sea",
        "draw",
        "left",
        "late",
        "run",
        "don't",
        "while",
        "press",
        "close",
        "night",
        "real",
        "life",
        "few",
        "north",
        "open",
        "seem",
        "together",
        "next",
        "white",
        "children",
        "begin",
        "got",
        "walk",
        "example",
        "ease",
        "paper",
        "group",
        "always",
        "music",
        "those",
        "both",
        "mark",
        "often",
        "letter",
        "until",
        "mile",
        "river",
        "car",
        "feet",
        "care",
        "second",
        "book",
        "carry",
        "took",
        "science",
        "eat",
        "room",
        "friend",
        "began",
        "idea",
        "fish",
        "mountain",
        "stop",
        "once",
        "base",
        "hear",
        "horse",
        "cut",
        "sure",
        "watch",
        "color",
        "face",
        "wood",
        "main",
        "enough",
        "plain",
        "girl",
        "usual",
        "young",
        "ready",
        "above",
        "ever",
        "red",
        "list",
        "though",
        "feel",
        "talk",
        "bird",
        "soon",
        "body",
        "dog",
        "family",
        "direct",
        "pose",
        "leave",
        "song",
        "measure",
        "door",
        "product",
        "black",
        "short",
        "numeral",
        "class",
        "wind",
        "question",
        "happen",
        "complete",
        "ship",
        "area",
        "half",
        "rock",
        "order",
        "fire",
        "south",
        "problem",
        "piece",
        "told",
        "knew",
        "pass",
        "since",
        "top",
        "whole",
        "king",
        "space",
        "heard",
        "best",
        "hour",
        "better",
        "true",
        "during",
        "hundred",
        "five",
        "remember",
        "step",
        "early",
        "hold",
        "west",
        "ground",
        "interest",
        "reach",
        "fast",
        "verb",
        "sing",
        "listen",
        "six",
        "table",
        "travel",
        "less",
        "morning",
        "ten",
        "simple",
        "several",
        "vowel",
        "toward",
        "war",
        "lay",
        "against",
        "pattern",
        "slow",
        "center",
        "love",
        "person",
        "money",
        "serve",
        "appear",
        "road",
        "map",
        "rain",
        "rule",
        "govern",
        "pull",
        "cold",
        "notice",
        "voice",
        "unit",
        "power",
        "town",
        "fine",
        "certain",
        "fly",
        "fall",
        "lead",
        "cry",
        "dark",
        "machine",
        "note",
        "wait",
        "plan",
        "figure",
        "star",
        "box",
        "noun",
        "field",
        "rest",
        "correct",
        "able",
        "pound",
        "done",
        "beauty",
        "drive",
        "stood",
        "contain",
        "front",
        "teach",
        "week",
        "final",
        "gave",
        "green",
        "oh",
        "quick",
        "develop",
        "ocean",
        "warm",
        "free",
        "minute",
        "strong",
        "special",
        "mind",
        "behind",
        "clear",
        "tail",
        "produce",
        "fact",
        "street",
        "inch",
        "multiply",
        "nothing",
        "course",
        "stay",
        "wheel",
        "full",
        "force",
        "blue",
        "object",
        "decide",
        "surface",
        "deep",
        "moon",
        "island",
        "foot",
        "system",
        "busy",
        "test",
        "record",
        "boat",
        "common",
        "gold",
        "possible",
        "plane",
        "stead",
        "dry",
        "wonder",
        "laugh",
        "thousand",
        "ago",
        "ran",
        "check",
        "game",
        "shape",
        "equate",
        "hot",
        "miss",
        "brought",
        "heat",
        "snow",
        "tire",
        "bring",
        "yes",
        "distant",
        "fill",
        "east",
        "paint",
        "language",
        "among",
        "grand",
        "ball",
        "yet",
        "wave",
        "drop",
        "heart",
        "am",
        "present",
        "heavy",
        "dance",
        "engine",
        "position",
        "arm",
        "wide",
        "sail",
        "material",
        "size",
        "vary",
        "settle",
        "speak",
        "weight",
        "general",
        "ice",
        "matter",
        "circle",
        "pair",
        "include",
        "divide",
        "syllable",
        "felt",
        "perhaps",
        "pick",
        "sudden",
        "count",
        "square",
        "reason",
        "length",
        "represent",
        "art",
        "subject",
        "region",
        "energy",
        "hunt",
        "probable",
        "bed",
        "brother",
        "egg",
        "ride",
        "cell",
        "believe",
        "fraction",
        "forest",
        "sit",
        "race",
        "window",
        "store",
        "summer",
        "train",
        "sleep",
        "prove",
        "lone",
        "leg",
        "exercise",
        "wall",
        "catch",
        "mount",
        "wish",
        "sky",
        "board",
        "joy",
        "winter",
        "sat",
        "written",
        "wild",
        "instrument",
        "kept",
        "glass",
        "grass",
        "cow",
        "job",
        "edge",
        "sign",
        "visit",
        "past",
        "soft",
        "fun",
        "bright",
        "gas",
        "weather",
        "month",
        "million",
        "bear",
        "finish",
        "happy",
        "hope",
        "flower",
        "clothe",
        "strange",
        "gone",
        "jump",
        "baby",
        "eight",
        "village",
        "meet",
        "root",
        "buy",
        "raise",
        "solve",
        "metal",
        "whether",
        "push",
        "seven",
        "paragraph",
        "third",
        "shall",
        "held",
        "hair",
        "describe",
        "cook",
        "floor",
        "either",
        "result",
        "burn",
        "hill",
        "safe",
        "cat",
        "century",
        "consider",
        "type",
        "law",
        "bit",
        "coast",
        "copy",
        "phrase",
        "silent",
        "tall",
        "sand",
        "soil",
        "roll",
        "temperature",
        "finger",
        "industry",
        "value",
        "fight",
        "lie",
        "beat",
        "excite",
        "natural",
        "view",
        "sense",
        "ear",
        "else",
        "quite",
        "broke",
        "case",
        "middle",
        "kill",
        "son",
        "lake",
        "moment",
        "scale",
        "loud",
        "spring",
        "observe",
        "child",
        "straight",
        "consonant",
        "nation",
        "dictionary",
        "milk",
        "speed",
        "method",
        "organ",
        "pay",
        "age",
        "section",
        "dress",
        "cloud",
        "surprise",
        "quiet",
        "stone",
        "tiny",
        "climb",
        "cool",
        "design",
        "poor",
        "lot",
        "experiment",
        "bottom",
        "key",
        "iron",
        "single",
        "stick",
        "flat",
        "twenty",
        "skin",
        "smile",
        "crease",
        "hole",
        "trade",
        "melody",
        "trip",
        "office",
        "receive",
        "row",
        "mouth",
        "exact",
        "symbol",
        "die",
        "least",
        "trouble",
        "shout",
        "except",
        "wrote",
        "seed",
        "tone",
        "join",
        "suggest",
        "clean",
        "break",
        "lady",
        "yard",
        "rise",
        "bad",
        "blow",
        "oil",
        "blood",
        "touch",
        "grew",
        "cent",
        "mix",
        "team",
        "wire",
        "cost",
        "lost",
        "brown",
        "wear",
        "garden",
        "equal",
        "sent",
        "choose",
        "fell",
        "fit",
        "flow",
        "fair",
        "bank",
        "collect",
        "save",
        "control",
        "decimal",
        "gentle",
        "woman",
        "captain",
        "practice",
        "separate",
        "difficult",
        "doctor",
        "please",
        "protect",
        "noon",
        "whose",
        "locate",
        "ring",
        "character",
        "insect",
        "caught",
        "period",
        "indicate",
        "radio",
        "spoke",
        "atom",
        "human",
        "history",
        "effect",
        "electric",
        "expect",
        "crop",
        "modern",
        "element",
        "hit",
        "student",
        "corner",
        "party",
        "supply",
        "bone",
        "rail",
        "imagine",
        "provide",
        "agree",
        "thus",
        "capital",
        "won't",
        "chair",
        "danger",
        "fruit",
        "rich",
        "thick",
        "soldier",
        "process",
        "operate",
        "guess",
        "necessary",
        "sharp",
        "wing",
        "create",
        "neighbor",
        "wash",
        "bat",
        "rather",
        "crowd",
        "corn",
        "compare",
        "poem",
        "string",
        "bell",
        "depend",
        "meat",
        "rub",
        "tube",
        "famous",
        "dollar",
        "stream",
        "fear",
        "sight",
        "thin",
        "triangle",
        "planet",
        "hurry",
        "chief",
        "colony",
        "clock",
        "mine",
        "tie",
        "enter",
        "major",
        "fresh",
        "search",
        "send",
        "yellow",
        "gun",
        "allow",
        "print",
        "dead",
        "spot",
        "desert",
        "suit",
        "current",
        "lift",
        "rose",
        "continue",
        "block",
        "chart",
        "hat",
        "sell",
        "success",
        "company",
        "subtract",
        "event",
        "particular",
        "deal",
        "swim",
        "term",
        "opposite",
        "wife",
        "shoe",
        "shoulder",
        "spread",
        "arrange",
        "camp",
        "invent",
        "cotton",
        "born",
        "determine",
        "quart",
        "nine",
        "truck",
        "noise",
        "level",
        "chance",
        "gather",
        "shop",
        "stretch",
        "throw",
        "shine",
        "property",
        "column",
        "molecule",
        "select",
        "wrong",
        "gray",
        "repeat",
        "require",
        "broad",
        "prepare",
        "salt",
        "nose",
        "plural",
        "anger",
        "claim",
        "continent",
        "oxygen",
        "sugar",
        "death",
        "pretty",
        "skill",
        "women",
        "season",
        "solution",
        "magnet",
        "silver",
        "thank",
        "branch",
        "match",
        "suffix",
        "especially",
        "fig",
        "afraid",
        "huge",
        "sister",
        "steel",
        "discuss",
        "forward",
        "similar",
        "guide",
        "experience",
        "score",
        "apple",
        "bought",
        "led",
        "pitch",
        "coat",
        "mass",
        "card",
        "band",
        "rope",
        "slip",
        "win",
        "dream",
        "evening",
        "condition",
        "feed",
        "tool",
        "total",
        "basic",
        "smell",
        "valley",
        "nor",
        "double",
        "seat",
        "arrive",
        "master",
        "track",
        "parent",
        "shore",
        "division",
        "sheet",
        "substance",
        "favor",
        "connect",
        "post",
        "spend",
        "chord",
        "fat",
        "glad",
        "original",
        "share",
        "station",
        "dad",
        "bread",
        "charge",
        "proper",
        "bar",
        "offer",
        "segment",
        "slave",
        "duck",
        "instant",
        "market",
        "degree",
        "populate",
        "chick",
        "dear",
        "enemy",
        "reply",
        "drink",
        "occur",
        "support",
        "speech",
        "nature",
        "range",
        "steam",
        "motion",
        "path",
        "liquid",
        "log",
        "meant",
        "quotient",
        "teeth",
        "shell",
        "neck",
    ];
    return words[i % words.length];
}
